import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Skeleton,
  Tag,
  Text,
  VStack,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MdLocationOn,
  MdPhone,
  MdEmail,
  MdNewspaper,
  MdCompare,
} from "react-icons/md";
import { FaWhatsapp, FaGlobe, FaSeedling } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getData,
  getDataById,
  postData,
} from "../../redux/actions/common.action";
import bottomImage from "../../icons/pdfbg.png";
import html2pdf from "html2pdf.js";
import "./proposal.css";
import {
  FaCheck,
  FaDownload,
  FaPrint,
  FaTimes,
  FaEye,
  FaList,
} from "react-icons/fa";
import { A4ResponsiveWrapper } from "./components/A4ResponsiveWrapper";
import {
  ArrowLeft,
  ChevronRight,
  Home,
  FileText,
  Menu,
  Download,
  Printer,
  Send,
} from "lucide-react";
import HtmlPaginated from "./components/HtmlPaginated ";
import ProposalInfoSkeleton from "./components/ProposalInfoSkeleton ";
import TermsAndSignaturePage from "./components/TermsAndSignaturePage";

interface ProposalDetails {
  proposal: {
    name: string;
    email: string;
    phone: string;
    status: string;
    sentDate: Date;
    clientReactionDate: Date;
    clientNote: string;
    Esign: string;
    location: string;
    greetingId: string;
    currency: string;
    descriptions: {
      value: string;
    };
    terms: {
      value: string;
    };
    emiSteps: {
      label: string;
      payPercentage: number;
      pay: number;
      isPaid: boolean;
      remark: string;
      emiPaymentReceivedDate: Date;
      emiPaymentDate: Date;
      emiPayUpdatedBy: {
        name: string;
        userId: string;
      };
    }[];
    discountType: string;
    subTotal: number;
    discount: string;
    discountPrice: number;
    total: number;
    totalTax: number;
    validTillDate: Date;
    proposalName: string;
    items: {
      productId: string;
      quantity: 1;
    }[];
    createdBY: {
      name: string;
      userId: string;
    };
  };

  courses: {
    CourseName: string;
  }[];
  signinuser: any;
  user: any;
  proposalGreeting: {
    _id: string;
    title: string;
    greeting: string;
  };
  loading: boolean;
}

export const A4Box = ({
  children,
  imageUrl,
  pageNumber,
  noPadding = false,
}: {
  children: React.ReactNode;
  imageUrl: any;
  pageNumber?: number;
  noPadding?: boolean;
}) => (
  <Box
    bg="white"
    w="794px"
    h="1122px"
    boxShadow="sm"
    px={noPadding ? 0 : { base: 6, md: 10 }}
    py={noPadding ? 0 : { base: 6, md: 10 }}
    pb={noPadding ? 0 : 12}
    position="relative"
    overflow="hidden"
    className="page"
    id={pageNumber ? `page-${pageNumber}` : undefined} // ✅ Add ID
  >
    {/* Background and content remain the same */}
    {!!imageUrl && (
      <Image
        src={imageUrl}
        alt="Background"
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width="60%"
        height="auto"
        objectFit="contain"
        opacity={0.08}
        zIndex={0}
        pointerEvents="none"
        crossOrigin="anonymous"
      />
    )}

    {pageNumber && (
      <Text
        position="absolute"
        bottom={3}
        right={6}
        fontSize="10px"
        color="gray.400"
        zIndex={1}
        fontWeight="500"
      >
        {pageNumber}
      </Text>
    )}

    <Box position="relative" zIndex={1} h="full">
      {children}
    </Box>
  </Box>
);

const ProposalView = () => {
  const { proposal, courses, signinuser, user, proposalGreeting, loading } =
    useSelector((state: { common: ProposalDetails }) => state.common);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get("proposalId");
  const sendId = searchParams.get("sendId");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSendModalOpen,
    onOpen: onSendModalOpen,
    onClose: onSendModalClose,
  } = useDisclosure();

  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const pageRefs = useRef<{ [key: number]: HTMLElement | null }>({});

  useEffect(() => {
    dispatch(getData("course"));
  }, []);

  useEffect(() => {
    if (!proposal) return;
    dispatch(getDataById(proposal?.greetingId, "proposalGreeting"));
  }, [proposal]);

  useEffect(() => {
    if (signinuser?.UserType === 2) {
      dispatch(getDataById(signinuser?.userId, "user"));
    } else if (signinuser?.UserType === 3) {
      dispatch(getDataById(signinuser?.userId, "user")).then(() => {
        dispatch(getDataById(user?.parentId, "user"));
      });
    }
  }, [signinuser]);

  useEffect(() => {
    if (!proposalId) return;
    dispatch(getDataById(proposalId, "proposal"));
  }, [proposalId]);

  // Scroll spy: update side menu highlight while scrolling pages
  useEffect(() => {
    // Wait until page nodes exist in the DOM
    const pageEls = Array.from({ length: totalPages }, (_, i) =>
      document.getElementById(`page-${i + 1}`),
    ).filter(Boolean) as HTMLElement[];

    if (pageEls.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // pick the most visible intersecting page
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));

        const top = visible[0];
        if (!top?.target) return;

        const id = (top.target as HTMLElement).id || "";
        const m = id.match(/page-(\d+)/);
        if (!m) return;
        const pageNum = Number(m[1]);
        if (!Number.isFinite(pageNum)) return;
        setCurrentPage((prev) => (prev === pageNum ? prev : pageNum));
      },
      {
        // account for sticky header (and give some breathing room)
        root: null,
        rootMargin: "-140px 0px -55% 0px",
        threshold: [0.15, 0.25, 0.35, 0.5, 0.65, 0.8],
      },
    );

    pageEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
    // totalPages is constant; observer should re-init only when pages re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeneratingPDF]);

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    await new Promise((resolve) => setTimeout(resolve, 100));

    const element = document.querySelector(".print-area");

    html2pdf()
      .set({
        margin: 0,
        filename: `${proposal?.proposalName || "proposal"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "px", format: [794, 1122], orientation: "portrait" },
      })
      .from(element)
      .save()
      .then(() => {
        setIsGeneratingPDF(false);
        toast({
          title: "PDF Downloaded Successfully",
          status: "success",
          duration: 3000,
          position: "top-right",
        });
      });
  };

  const handleSendProposalPDF = async () => {
    if (!sendId) return;
    dispatch(postData({ id: sendId }, "client-proposal", navigate, toast));
    onSendModalClose(); // Close modal after sending
  };

  const handleOpenSendModal = () => {
    onSendModalOpen(); // Open confirmation modal
  };

  const handleBack = () => {
    navigate("/proposal-list");
  };

  const scrollToPage = (pageNum: number) => {
    // Close drawer first
    onClose();

    // Small delay to allow drawer to close
    setTimeout(() => {
      // Try multiple methods to find the element
      const element =
        pageRefs.current[pageNum] ||
        document.getElementById(`page-${pageNum}`) ||
        document.querySelector(`[id="page-${pageNum}"]`);

      if (element) {
        // Get the header height to offset scroll
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        setCurrentPage(pageNum);
      } else {
        console.error(`Page ${pageNum} element not found`);
      }
    }, 100); // Wait for drawer close animation
  };

  // Calculate total pages
  const totalPages = 5; // Greeting, Description, Items, Payment, Terms

  return (
    <Box bg="gray.50" minH="100vh" overflow="hidden">
      {/* Modern Header - Hidden in print */}
      <Box
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
        className="no-print"
      >
        <Flex
          justify="space-between"
          align="center"
          px={{ base: 4, md: 8 }}
          py={4}
          maxW="1400px"
          mx="auto"
        >
          {/* Left - Back & Breadcrumb */}
          <VStack align="start" spacing={2}>
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={handleBack}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize="sm"
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              Back
            </Button>

            <Breadcrumb
              spacing={2}
              separator={<ChevronRight size={12} color="#9CA3AF" />}
              fontSize="xs"
              color="gray.600"
              display={{ base: "none", md: "flex" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBack}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                >
                  <Home size={12} />
                  <Text>Proposals</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <FileText size={12} />
                  <Text>Preview</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </VStack>

          {/* Right - Actions */}
          <HStack spacing={2}>
            {/* Single Menu Button - Opens Drawer */}
            <Button
              leftIcon={<Menu size={16} />}
              onClick={onOpen}
              variant="outline"
              colorScheme="gray"
              size={{ base: "sm", md: "md" }}
              display={{ base: "flex", lg: "none" }} // Show only on mobile/tablet
              fontSize="sm"
            >
              <Text display={{ base: "none", md: "inline" }}>Pages</Text>
            </Button>

            <Button
              leftIcon={<Printer size={16} />}
              onClick={handlePrint}
              variant="outline"
              colorScheme="blue"
              size={{ base: "sm", md: "md" }}
              fontSize="sm"
            >
              <Text display={{ base: "none", md: "inline" }}>Print</Text>
            </Button>

            <Button
              leftIcon={<Download size={16} />}
              onClick={handleDownloadPDF}
              variant="outline"
              colorScheme="blue"
              size={{ base: "sm", md: "md" }}
              isLoading={isGeneratingPDF}
              fontSize="sm"
            >
              <Text display={{ base: "none", md: "inline" }}>Download</Text>
            </Button>

            {sendId && (
              <Button
                leftIcon={<Send size={16} />}
                onClick={handleOpenSendModal} // Changed from handleSendProposalPDF
                colorScheme="green"
                size={{ base: "sm", md: "md" }}
                isLoading={loading}
                fontSize="sm"
                fontWeight="700"
              >
                Send
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Page Navigator Drawer - For Mobile/Tablet */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        size={{ base: "xs", md: "sm" }}
      >
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient="linear(to-r, blue.500, blue.600)"
            color="white"
          >
            <HStack spacing={2}>
              <FileText size={20} />
              <Text>Page Navigator</Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={4}>
            <VStack spacing={3} align="stretch">
              {[
                { num: 1, title: "Greeting" },
                { num: 2, title: "Description" },
                { num: 3, title: "Items" },
                { num: 4, title: "Payment" },
                { num: 5, title: "Terms" },
              ].map((page) => (
                <Box
                  key={page.num}
                  onClick={() => scrollToPage(page.num)}
                  cursor="pointer"
                  bg="white"
                  border="2px solid"
                  borderColor={
                    currentPage === page.num ? "blue.500" : "gray.200"
                  }
                  borderRadius="lg"
                  p={3}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: "blue.400",
                    transform: "translateX(4px)",
                    boxShadow: "md",
                  }}
                  _active={{
                    transform: "scale(0.98)",
                  }}
                >
                  <HStack spacing={2}>
                    <Box
                      bg={currentPage === page.num ? "blue.500" : "gray.200"}
                      color={currentPage === page.num ? "white" : "gray.600"}
                      w={8}
                      h={8}
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {page.num}
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="700" color="gray.800">
                        Page {page.num}
                      </Text>
                      <Text fontSize="xs" fontWeight="500" color="gray.600">
                        {page.title}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content with Sidebar */}
      <Flex
        maxW="1400px"
        mx="auto"
        gap={4}
        p={{ base: 2, md: 4 }}
        overflow="hidden"
      >
        {/* Left Sidebar - Desktop Only */}
        <Box
          w="200px"
          display={{ base: "none", lg: "block" }} // Show only on desktop (lg+)
          position="fixed"
          top="180px"
          h="calc(100vh - 140px)"
          overflowY="auto"
          className="no-print"
          css={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              bg: "gray.100",
            },
            "&::-webkit-scrollbar-thumb": {
              bg: "gray.400",
              borderRadius: "full",
            },
          }}
          visibility={{ base: "hidden", md: "visible" }}
        >
          <VStack spacing={3} align="stretch" p={2}>
            {[
              { num: 1, title: "Greeting" },
              { num: 2, title: "Description" },
              { num: 3, title: "Items" },
              { num: 4, title: "Payment" },
              { num: 5, title: "Terms" },
            ].map((page) => (
              <Box
                key={page.num}
                onClick={() => scrollToPage(page.num)}
                cursor="pointer"
                bg="white"
                border="2px solid"
                borderColor={currentPage === page.num ? "blue.500" : "gray.200"}
                borderRadius="lg"
                p={3}
                transition="all 0.2s"
                _hover={{
                  borderColor: "blue.400",
                  transform: "translateX(4px)",
                  boxShadow: "md",
                }}
              >
                <HStack spacing={2}>
                  <Box
                    bg={currentPage === page.num ? "blue.500" : "gray.200"}
                    color={currentPage === page.num ? "white" : "gray.600"}
                    w={6}
                    h={6}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {page.num}
                  </Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.700">
                    {page.title}
                  </Text>
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Main Preview Area */}
        <Box flex={1} overflow="hidden">
          <VStack
            align="center"
            gap={isGeneratingPDF ? 0 : 4}
            spacing={0}
            pb={8}
          >
            <A4ResponsiveWrapper isDownload={isGeneratingPDF}>
              {/* Page 1: Modern Gradient Design - CLEAN ICON BADGES */}
              <Box
                position="relative"
                w="794px"
                h="1122px"
                bg="white"
                boxShadow="sm"
                className="page"
                overflow="hidden"
                id="page-1"
              >
                <Box ref={(el) => (pageRefs.current[1] = el)} h="full">
                  {loading ? (
                    <Box px={10} py={10}>
                      <ProposalInfoSkeleton />
                    </Box>
                  ) : (
                    <>
                      {/* Background Watermark */}
                      {!!user?.ComapanyImageTwo && (
                        <Image
                          src={user?.ComapanyImageTwo}
                          alt="Background"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          width="70%"
                          height="auto"
                          objectFit="contain"
                          opacity={0.08}
                          zIndex={0}
                          pointerEvents="none"
                          crossOrigin="anonymous"
                        />
                      )}

                      {/* GRADIENT HEADER WITH WAVE */}
                      <Box
                        position="relative"
                        bgGradient="linear(to-br, blue.600, purple.600, pink.500)"
                        overflow="hidden"
                        zIndex={1}
                      >
                        {/* Animated Background Blobs */}
                        <Box
                          position="absolute"
                          top="-20%"
                          right="0%"
                          w="180px"
                          h="180px"
                          borderRadius="full"
                          bg="whiteAlpha.100"
                          filter="blur(50px)"
                        />
                        <Box
                          position="absolute"
                          bottom="-10%"
                          left="10%"
                          w="150px"
                          h="150px"
                          borderRadius="full"
                          bg="whiteAlpha.100"
                          filter="blur(40px)"
                        />

                        {/* Header Content */}
                        <Box position="relative" zIndex={1} px={10} py={8}>
                          {/* Company Logo */}
                          <Image
                            src={signinuser?.ComapanyImageOne}
                            w="140px"
                            mb={4}
                            alt="Company Logo"
                            rounded={"lg"}
                          />

                          {/* Company Name Badge - CLEAN SIMPLE */}
                          {/* <HStack spacing={2} display="inline-flex">
                            <Icon as={MdNewspaper} boxSize={4} color="white" />
                            <HStack align="start" spacing={0} flex={1} minW={0}>
                              <Text
                                fontSize="sm"
                                color="white"
                                fontWeight="700"
                              >
                                {user?.companyName}
                              </Text>
                            </HStack>
                          </HStack> */}
                          <HStack spacing={2} w="full" align="flex-start">
                            <Icon
                              as={MdCompare}
                              boxSize={6}
                              color="white"
                              flexShrink={0}
                              mt={0.5}
                            />
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              {/* <Text
                                fontSize="10px"
                                fontWeight="600"
                                color="white"
                              >
                                WE ARE,
                              </Text> */}
                              <Text
                                fontSize="14px"
                                fontWeight="700"
                                color="white"
                                wordBreak="break-word"
                                w="full"
                              >
                                {user?.companyName}
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>

                        {/* Bottom Wave */}
                        <Box
                          position="absolute"
                          bottom="-2px"
                          left="0"
                          right="0"
                          height="30px"
                        >
                          <svg
                            viewBox="0 0 1200 30"
                            preserveAspectRatio="none"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <path
                              d="M0,15 Q300,0 600,15 T1200,15 L1200,30 L0,30 Z"
                              fill="white"
                            />
                          </svg>
                        </Box>
                      </Box>

                      {/* Main Content */}
                      <Box
                        w="full"
                        px={10}
                        pt={6}
                        pb="170px"
                        position="relative"
                        zIndex={1}
                      >
                        {/* LEFT SIDE - Client Info Card */}
                        <VStack
                          align="stretch"
                          spacing={0}
                          w="280px"
                          flexShrink={0}
                        >
                          {/* Client Card */}
                          <Box
                            bgGradient="linear(to-br, blue.50, purple.50)"
                            border="2px solid"
                            borderColor="blue.100"
                            borderRadius="2xl"
                            p={5}
                            position="relative"
                            overflow="hidden"
                          >
                            {/* Card Background Accent */}
                            <Box
                              position="absolute"
                              top="-40%"
                              right="-40%"
                              w="200px"
                              h="200px"
                              borderRadius="full"
                              bg="blue.100"
                              opacity={0.1}
                              zIndex={0}
                            />

                            <VStack
                              align="start"
                              spacing={3}
                              position="relative"
                              zIndex={1}
                            >
                              {/* Dear Text */}
                              <Box>
                                <Text
                                  fontSize="9px"
                                  fontWeight="700"
                                  color="blue.600"
                                  mb={1}
                                >
                                  RECIPIENT
                                </Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="700"
                                  color="gray.900"
                                  lineHeight="1.3"
                                >
                                  Dear,{" "}
                                  <Text as="span" color="blue.600">
                                    {proposal?.name}
                                  </Text>
                                </Text>
                              </Box>

                              <Divider borderColor="blue.200" />

                              {/* Contact Details */}
                              <VStack align="start" spacing={2} w="full">
                                {/* Email */}
                                <HStack spacing={2} w="full" align="flex-start">
                                  <Icon
                                    as={MdEmail}
                                    boxSize={4}
                                    color="blue.600"
                                    flexShrink={0}
                                    mt={0.5}
                                  />
                                  <VStack
                                    align="start"
                                    spacing={0}
                                    flex={1}
                                    minW={0}
                                  >
                                    <Text
                                      fontSize="8px"
                                      fontWeight="600"
                                      color="gray.500"
                                    >
                                      EMAIL
                                    </Text>
                                    <Text
                                      fontSize="9px"
                                      fontWeight="500"
                                      color="gray.800"
                                      wordBreak="break-word"
                                      w="full"
                                    >
                                      {proposal?.email}
                                    </Text>
                                  </VStack>
                                </HStack>

                                {/* Phone */}
                                <HStack spacing={2} w="full">
                                  <Icon
                                    as={MdPhone}
                                    boxSize={4}
                                    color="green.600"
                                    flexShrink={0}
                                  />
                                  <VStack
                                    align="start"
                                    spacing={0}
                                    flex={1}
                                    minW={0}
                                  >
                                    <Text
                                      fontSize="8px"
                                      fontWeight="600"
                                      color="gray.500"
                                    >
                                      PHONE
                                    </Text>
                                    <Text
                                      fontSize="10px"
                                      fontWeight="500"
                                      color="gray.800"
                                    >
                                      {proposal?.phone}
                                    </Text>
                                  </VStack>
                                </HStack>

                                {/* Address */}
                                <HStack spacing={2} align="flex-start" w="full">
                                  <Icon
                                    as={MdLocationOn}
                                    boxSize={4}
                                    color="orange.600"
                                    flexShrink={0}
                                    mt={0.5}
                                  />
                                  <VStack
                                    align="start"
                                    spacing={0}
                                    flex={1}
                                    minW={0}
                                  >
                                    <Text
                                      fontSize="8px"
                                      fontWeight="600"
                                      color="gray.500"
                                    >
                                      ADDRESS
                                    </Text>
                                    <Text
                                      fontSize="10px"
                                      fontWeight="500"
                                      color="gray.800"
                                      lineHeight="1.3"
                                    >
                                      {proposal?.location}
                                    </Text>
                                  </VStack>
                                </HStack>
                              </VStack>
                            </VStack>
                          </Box>
                        </VStack>

                        <Divider borderColor="gray.300" my={4} />

                        {/* Greeting Content */}
                        <Box
                          fontSize="13px"
                          lineHeight="1.4"
                          color="gray.700"
                          mb={4}
                          dangerouslySetInnerHTML={{
                            __html: proposalGreeting?.greeting || "",
                          }}
                          sx={{
                            "& p": { mb: 2, lineHeight: "1.4" },
                            "& strong": {
                              fontWeight: "700",
                              color: "gray.900",
                            },
                            "& ul, & ol": { pl: 5, mb: 2 },
                            "& li": { mb: 1, lineHeight: "1.4" },
                          }}
                        />

                        {/* Signature */}
                        <Box mt={4}>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.800"
                            lineHeight="1.4"
                          >
                            Kind Regards,
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            color="gray.900"
                            lineHeight="1.4"
                          >
                            {user?.Name}
                          </Text>
                          <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                            Mob: {user?.Phone}
                          </Text>
                          <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                            Email: {user?.Email}
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.800"
                            mt={1}
                            lineHeight="1.4"
                          >
                            {user?.companyName}
                          </Text>
                        </Box>
                      </Box>

                      {/* GRADIENT FOOTER - CLEAN ICON HEADERS */}
                      <Box
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        w="full"
                        bgGradient="linear(to-br, blue.600, purple.600, pink.500)"
                        overflow="hidden"
                        zIndex={1}
                      >
                        <Box
                          position="absolute"
                          top="-30%"
                          right="-10%"
                          w="200px"
                          h="200px"
                          borderRadius="full"
                          bg="whiteAlpha.100"
                          filter="blur(40px)"
                        />
                        <Box
                          position="absolute"
                          bottom="-20%"
                          left="-5%"
                          w="180px"
                          h="180px"
                          borderRadius="full"
                          bg="whiteAlpha.100"
                          filter="blur(35px)"
                        />

                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          height="20px"
                        >
                          <svg
                            viewBox="0 0 1200 20"
                            preserveAspectRatio="none"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <path
                              d="M0,10 Q300,0 600,10 T1200,10 L1200,0 L0,0 Z"
                              fill="white"
                            />
                          </svg>
                        </Box>

                        <Box
                          position="relative"
                          zIndex={1}
                          px={6}
                          py={5}
                          pt={7}
                        >
                          <Grid
                            templateColumns="repeat(3, 1fr)"
                            gap={4}
                            fontSize="xs"
                            color="white"
                          >
                            {/* Address Column */}
                            <VStack align="start" spacing={1.5}>
                              <HStack spacing={2}>
                                <Icon
                                  as={MdLocationOn}
                                  boxSize={4}
                                  color="white"
                                  mt={0.5}
                                />
                                <Text fontWeight="700" fontSize="11px">
                                  Address
                                </Text>
                              </HStack>
                              <Text
                                lineHeight="1.4"
                                opacity={0.95}
                                fontSize="9px"
                                fontWeight="500"
                              >
                                {user?.Address}
                              </Text>
                            </VStack>

                            {/* Contact Column */}
                            <VStack align="start" spacing={1.5}>
                              <HStack spacing={2}>
                                <Icon
                                  as={MdPhone}
                                  boxSize={4}
                                  color="white"
                                  mt={0.5}
                                />
                                <Text fontWeight="700" fontSize="11px">
                                  Contact
                                </Text>
                              </HStack>
                              <VStack align="start" spacing={1}>
                                <HStack spacing={1}>
                                  <Icon
                                    as={MdPhone}
                                    boxSize={2.5}
                                    opacity={0.8}
                                  />
                                  <Text
                                    opacity={0.95}
                                    fontSize="9px"
                                    fontWeight="500"
                                  >
                                    {user?.Phone}
                                  </Text>
                                </HStack>
                                <HStack spacing={1}>
                                  <Icon
                                    as={FaWhatsapp}
                                    boxSize={2.5}
                                    color="green.300"
                                  />
                                  <Text
                                    opacity={0.95}
                                    fontSize="9px"
                                    fontWeight="500"
                                  >
                                    {user?.Phone}
                                  </Text>
                                </HStack>
                              </VStack>
                            </VStack>

                            {/* Email Column */}
                            <VStack align="start" spacing={1.5}>
                              <HStack spacing={2}>
                                <Icon
                                  as={MdEmail}
                                  boxSize={4}
                                  color="white"
                                  mt={0.5}
                                />
                                <Text fontWeight="700" fontSize="11px">
                                  Contact Info
                                </Text>
                              </HStack>
                              <VStack align="start" spacing={0.5} w="full">
                                <HStack spacing={1} w="full" minW={0}>
                                  <Icon
                                    as={MdEmail}
                                    boxSize={2.5}
                                    opacity={0.8}
                                    flexShrink={0}
                                  />
                                  <Text
                                    opacity={0.95}
                                    fontSize="9px"
                                    fontWeight="500"
                                    wordBreak="break-word"
                                    w="full"
                                  >
                                    {user?.Email}
                                  </Text>
                                </HStack>
                                <HStack spacing={1} w="full" minW={0}>
                                  <Icon
                                    as={FaGlobe}
                                    boxSize={2.5}
                                    opacity={0.8}
                                    flexShrink={0}
                                  />
                                  <Text
                                    opacity={0.95}
                                    fontSize="9px"
                                    fontWeight="500"
                                    wordBreak="break-word"
                                    w="full"
                                  >
                                    {user?.webURL}
                                  </Text>
                                </HStack>
                              </VStack>
                            </VStack>
                          </Grid>

                          <Divider
                            borderColor="whiteAlpha.300"
                            my={3}
                            opacity={0.5}
                          />

                          <HStack justify="center" spacing={2}>
                            <Text
                              fontSize="8px"
                              color="whiteAlpha.800"
                              fontWeight="600"
                              textAlign="center"
                            >
                              © 2025 {user?.companyName}. All rights reserved.
                            </Text>
                          </HStack>
                        </Box>
                      </Box>

                      {/* Page Number */}
                      <Text
                        position="absolute"
                        bottom="155px"
                        right={6}
                        fontSize="10px"
                        color="gray.400"
                        zIndex={2}
                        fontWeight="500"
                      >
                        1
                      </Text>
                    </>
                  )}
                </Box>
              </Box>

              {/* Page 2: Proposal Description */}
              <Box id="page-2" ref={(el) => (pageRefs.current[2] = el)}>
                <HtmlPaginated
                  html={proposal?.descriptions?.value || ""}
                  isDownload={isGeneratingPDF}
                  pageNumber={2}
                />
              </Box>

              {/* Page 3: Items & Pricing */}
              <A4Box imageUrl={user?.ComapanyImageTwo} pageNumber={3}>
                <Box ref={(el) => (pageRefs.current[3] = el)}>
                  {/* Modern Header */}
                  <Flex align="center" gap={3} mb={6}>
                    <Box
                      bgGradient="linear(to-br, purple.400, purple.600)"
                      p={3}
                      borderRadius="xl"
                      boxShadow="md"
                    >
                      <FileText size={20} color="white" />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xl" fontWeight="bold" color="gray.800">
                        Proposal Items
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Products and services breakdown
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Items Table */}
                  <VStack spacing={0} mb={6}>
                    {/* Table Header */}
                    <SimpleGrid
                      columns={10}
                      w="100%"
                      bgGradient="linear(to-r, purple.500, purple.600)"
                      borderTopRadius="lg"
                      overflow="hidden"
                      fontWeight="bold"
                      fontSize="xs"
                      color="white"
                    >
                      <GridItem
                        py={3}
                        px={2}
                        textAlign="center"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        #
                      </GridItem>

                      <GridItem
                        colSpan={3}
                        py={3}
                        px={2}
                        borderRight="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        Product Name
                      </GridItem>

                      <GridItem
                        py={3}
                        px={2}
                        textAlign="center"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        Qty
                      </GridItem>

                      <GridItem
                        py={3}
                        px={2}
                        textAlign="center"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        Price
                      </GridItem>

                      <GridItem
                        colSpan={2}
                        py={3}
                        px={2}
                        textAlign="center"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.300"
                      >
                        Tax
                      </GridItem>

                      <GridItem colSpan={2} py={3} px={2} textAlign="right">
                        Total
                      </GridItem>
                    </SimpleGrid>

                    {/* Table Rows */}
                    {proposal?.items?.map((item: any, index: number) => (
                      <SimpleGrid
                        key={index}
                        columns={10}
                        w="100%"
                        bg={index % 2 === 0 ? "white" : "gray.50"}
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        fontSize="sm"
                        _last={{
                          borderBottomRadius: "lg",
                        }}
                      >
                        {/* # Column */}
                        <GridItem
                          py={4}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                        >
                          <Text
                            fontWeight="700"
                            fontSize="13px"
                            color="purple.600"
                          >
                            {index + 1}
                          </Text>
                        </GridItem>

                        {/* Product Name Column */}
                        <GridItem
                          colSpan={3}
                          py={4}
                          px={2}
                          borderRight="1px solid"
                          borderColor="gray.200"
                        >
                          <Text
                            fontWeight="600"
                            color="gray.800"
                            fontSize="13px"
                          >
                            {
                              courses?.find(
                                (product: any) =>
                                  product?._id === item?.productId
                              )?.CourseName
                            }
                          </Text>
                        </GridItem>

                        {/* Quantity Column */}
                        <GridItem
                          py={4}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                        >
                          <Text fontWeight="600" fontSize="13px">
                            {item?.quantity}
                          </Text>
                        </GridItem>

                        {/* Price Column */}
                        <GridItem
                          py={4}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                        >
                          <Text
                            fontWeight="600"
                            color="gray.700"
                            fontSize="13px"
                          >
                            {proposal?.currency}
                            {item?.price}
                          </Text>
                        </GridItem>

                        {/* Tax Column - PLAIN TEXT */}
                        <GridItem
                          colSpan={2}
                          py={4}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                        >
                          <Text
                            fontWeight="700"
                            fontSize="13px"
                            color="blue.600"
                          >
                            {item?.tax}
                          </Text>
                        </GridItem>

                        {/* Total Column */}
                        <GridItem
                          colSpan={2}
                          py={4}
                          px={2}
                          textAlign="right"
                          fontWeight="bold"
                          color="purple.600"
                          fontSize="13px"
                        >
                          {proposal?.currency}
                          {item?.totalWithTax?.toFixed(2)}
                        </GridItem>
                      </SimpleGrid>
                    ))}
                  </VStack>

                  {/* Summary Section */}
                  <Box
                    bg="purple.50"
                    border="2px solid"
                    borderColor="purple.200"
                    borderRadius="lg"
                    p={6}
                    mt={4}
                  >
                    {/* Sub Total */}
                    <Flex justify="space-between" mb={4} align="center">
                      <Text fontSize="16px" fontWeight="700" color="gray.800">
                        Sub Total
                      </Text>
                      <Text fontSize="16px" fontWeight="700" color="gray.800">
                        {proposal?.currency}
                        {proposal?.subTotal?.toFixed(2)}
                      </Text>
                    </Flex>

                    {/* Discount */}
                    <Flex justify="space-between" mb={4} align="center">
                      <HStack spacing={2}>
                        <Text fontSize="16px" fontWeight="700" color="gray.800">
                          Discount
                        </Text>
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color="green.600"
                        >
                          {proposal?.discountType === "percentage"
                            ? `${proposal?.discount}%`
                            : "Fixed"}
                        </Text>
                      </HStack>
                      <Text fontSize="16px" fontWeight="700" color="red.600">
                        -{proposal?.currency}
                        {proposal?.discountPrice?.toFixed(2)}
                      </Text>
                    </Flex>

                    <Divider borderColor="purple.300" mb={4} />

                    {/* Total Tax */}
                    <Flex justify="space-between" mb={4} align="center">
                      <Text fontSize="14px" fontWeight="600" color="gray.700">
                        Total Tax
                      </Text>
                      <Text fontSize="14px" fontWeight="600" color="gray.700">
                        {proposal?.currency}
                        {proposal?.totalTax?.toFixed(2)}
                      </Text>
                    </Flex>

                    {/* Grand Total */}
                    <Flex justify="space-between" align="center">
                      <Text
                        fontSize="18px"
                        fontWeight="900"
                        color={"purple.600"}
                      >
                        Total
                      </Text>
                      <Text
                        fontSize="18px"
                        fontWeight="900"
                        color={"purple.600"}
                      >
                        {proposal?.currency}
                        {proposal?.total?.toFixed(2)}
                      </Text>
                    </Flex>
                  </Box>
                </Box>
              </A4Box>

              {/* Page 4: Payment Plan */}
              <A4Box imageUrl={user?.ComapanyImageTwo} pageNumber={4}>
                <Box ref={(el) => (pageRefs.current[4] = el)}>
                  {/* Modern Header */}
                  <Flex align="center" gap={3} mb={6}>
                    <Box
                      bgGradient="linear(to-br, green.400, green.600)"
                      p={3}
                      borderRadius="xl"
                      boxShadow="md"
                    >
                      <Icon
                        viewBox="0 0 24 24"
                        boxSize={5}
                        color="white"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                      </Icon>
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xl" fontWeight="bold" color="gray.800">
                        Payment Plan
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        EMI schedule and payment tracking
                      </Text>
                    </VStack>
                  </Flex>

                  {/* EMI Table */}
                  <VStack spacing={0} mb={6}>
                    {/* Header */}
                    <SimpleGrid
                      w="100%"
                      bgGradient="linear(to-r, green.500, green.600)"
                      p={4}
                      borderTopRadius="lg"
                      columns={6}
                      alignItems="center"
                      fontWeight="bold"
                      fontSize="xs"
                      color="white"
                    >
                      <Text textAlign="center">#</Text>
                      <Text textAlign="center">EMI %</Text>
                      <Text textAlign="center">Amount</Text>
                      <Text textAlign="center">Status</Text>
                      <Text textAlign="center">Emi Payment Date</Text>
                      <Text textAlign="center">Paid Date</Text>
                    </SimpleGrid>

                    {/* Rows */}
                    {proposal?.emiSteps?.map((emi, idx) => (
                      <SimpleGrid
                        key={idx}
                        w="100%"
                        bg={idx % 2 === 0 ? "white" : "gray.50"}
                        p={4}
                        columns={6}
                        alignItems="center"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        fontSize="sm"
                        _last={{
                          borderBottomRadius: "lg",
                        }}
                      >
                        <Text textAlign="center" fontWeight="600">
                          {emi?.label}
                        </Text>
                        <Text
                          textAlign="center"
                          fontWeight="bold"
                          color="green.600"
                        >
                          {emi?.payPercentage}%
                        </Text>
                        <Text
                          textAlign="center"
                          fontWeight="bold"
                          color="gray.800"
                        >
                          {proposal?.currency}
                          {emi?.pay?.toFixed(2)}
                        </Text>
                        <Box display="flex" justifyContent="center">
                          <Text
                            fontWeight="700"
                            fontSize="12px"
                            color={emi?.isPaid ? "green.600" : "red.600"}
                            px={2}
                            py={1}
                          >
                            {emi?.isPaid ? "✓ PAID" : "✗ PENDING"}
                          </Text>
                        </Box>
                        <Text textAlign="center" color="gray.600" fontSize="xs">
                          {emi?.emiPaymentDate
                            ? new Date(emi?.emiPaymentDate).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )
                            : "--"}
                        </Text>
                        <Text textAlign="center" color="gray.600" fontSize="xs">
                          {emi?.isPaid && emi?.emiPaymentReceivedDate
                            ? new Date(
                                emi?.emiPaymentReceivedDate
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "--"}
                        </Text>
                      </SimpleGrid>
                    ))}
                  </VStack>

                  {/* Payment Summary Cards */}
                  <SimpleGrid columns={3} spacing={4}>
                    {/* Paid */}
                    <Box
                      p={4}
                      bg="white"
                      borderRadius="xl"
                      boxShadow="md"
                      border="2px solid"
                      borderColor="green.200"
                      position="relative"
                      overflow="hidden"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        w="full"
                        h="4px"
                        bgGradient="linear(to-r, green.400, green.600)"
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.500"
                        mb={2}
                      >
                        Paid Amount
                      </Text>
                      <Text fontSize="xl" fontWeight="900" color="green.600">
                        {proposal?.currency}
                        {proposal?.emiSteps
                          ?.filter((emi) => emi.isPaid)
                          ?.reduce((acc, curr) => acc + curr.pay, 0)
                          .toFixed(2)}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {
                          proposal?.emiSteps?.filter((emi) => emi.isPaid)
                            ?.length
                        }{" "}
                        payment(s) received
                      </Text>
                    </Box>

                    {/* Remaining */}
                    <Box
                      p={4}
                      bg="white"
                      borderRadius="xl"
                      boxShadow="md"
                      border="2px solid"
                      borderColor="red.200"
                      position="relative"
                      overflow="hidden"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        w="full"
                        h="4px"
                        bgGradient="linear(to-r, red.400, red.600)"
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.500"
                        mb={2}
                      >
                        Remaining
                      </Text>
                      <Text fontSize="xl" fontWeight="900" color="red.600">
                        {proposal?.currency}
                        {proposal?.emiSteps
                          ?.filter((emi) => !emi.isPaid)
                          ?.reduce((acc, curr) => acc + curr.pay, 0)
                          .toFixed(2)}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {
                          proposal?.emiSteps?.filter((emi) => !emi.isPaid)
                            ?.length
                        }{" "}
                        payment(s) pending
                      </Text>
                    </Box>

                    {/* Total */}
                    <Box
                      p={4}
                      bg="white"
                      borderRadius="xl"
                      boxShadow="md"
                      border="2px solid"
                      borderColor="blue.200"
                      position="relative"
                      overflow="hidden"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        w="full"
                        h="4px"
                        bgGradient="linear(to-r, blue.400, blue.600)"
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.500"
                        mb={2}
                      >
                        Total Amount
                      </Text>
                      <Text fontSize="xl" fontWeight="900" color="blue.600">
                        {proposal?.currency}
                        {proposal?.total?.toFixed(2)}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Full proposal value
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </A4Box>

              {/* Page 5: Terms & Conditions - AUTO PAGINATION */}
              <Box id="page-5" ref={(el) => (pageRefs.current[5] = el)}>
                <TermsAndSignaturePage
                  proposal={proposal}
                  user={user}
                  isDownload={isGeneratingPDF}
                />
              </Box>
            </A4ResponsiveWrapper>
          </VStack>
        </Box>
      </Flex>

      {/* Send Proposal Confirmation Modal */}
      <Modal
        isOpen={isSendModalOpen}
        onClose={onSendModalClose}
        size="md"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader
            bgGradient="linear(to-r, green.500, green.600)"
            color="white"
            borderTopRadius="xl"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Send size={20} />
            Confirm Sending Proposal
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: "green.400" }} />

          <ModalBody py={6}>
            <VStack align="start" spacing={4}>
              <Box
                p={4}
                bg="green.50"
                borderRadius="lg"
                border="2px solid"
                borderColor="green.200"
              >
                <Flex align="start" gap={3}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="green.500"
                    color="white"
                    w={8}
                    h={8}
                    borderRadius="full"
                    flexShrink={0}
                    fontWeight="bold"
                    fontSize="sm"
                  >
                    ✓
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="700" fontSize="sm" color="gray.800">
                      Proposal Details
                    </Text>
                    <VStack
                      align="start"
                      spacing={0.5}
                      fontSize="xs"
                      color="gray.600"
                    >
                      <Text>
                        <Text as="span" fontWeight="600">
                          Client:
                        </Text>{" "}
                        {proposal?.name}
                      </Text>
                      <Text>
                        <Text as="span" fontWeight="600">
                          Email:
                        </Text>{" "}
                        {proposal?.email}
                      </Text>
                      <Text>
                        <Text as="span" fontWeight="600">
                          Amount:
                        </Text>{" "}
                        {proposal?.currency}
                        {proposal?.total?.toFixed(2)}
                      </Text>
                    </VStack>
                  </VStack>
                </Flex>
              </Box>

              <Box
                p={4}
                bg="blue.50"
                borderRadius="lg"
                border="2px solid"
                borderColor="blue.200"
              >
                <Flex align="start" gap={3}>
                  <Icon
                    as={FileText}
                    boxSize={5}
                    color="blue.600"
                    flexShrink={0}
                  />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="700" fontSize="sm" color="gray.800">
                      This proposal will be sent to the client
                    </Text>
                    <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                      The client will receive an email with the proposal
                      attachment and details.
                    </Text>
                  </VStack>
                </Flex>
              </Box>

              <Box
                p={3}
                bg="orange.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="orange.200"
              >
                <Text fontSize="xs" color="orange.800" fontWeight="500">
                  <Text as="span" fontWeight="700">
                    Note:
                  </Text>{" "}
                  Make sure all proposal details are correct before sending.
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter
            gap={3}
            pt={4}
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <Button
              variant="ghost"
              onClick={onSendModalClose}
              size="md"
              colorScheme="gray"
            >
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSendProposalPDF}
              size="md"
              fontWeight="700"
              leftIcon={<Send size={16} />}
              isLoading={loading}
            >
              Send Proposal
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProposalView;
